import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import useMenu from "../hooks/useMenu";
import { isPlatformOperator } from "../utils/authUtils";
import SETTINGS_RESOURCES from "../pages/Settings/settingsResources";
import SidebarIcon from "./SidebarIcon";
import "./Sidebar.css";

function ChildLink({ item, onNavigate }) {
  return (
    <NavLink to={`/${item.url}`} className="sidebar-child-link" activeClassName="active" onClick={onNavigate}>
      {item.name}
    </NavLink>
  );
}

/** True if any of a nav group's leaf links is the page currently open — used to
 * default that one group open and leave its siblings collapsed, matching the
 * old app's own sidebar (only the section containing the current page starts
 * expanded, not everything at once). */
function groupContainsPath(navItem, pathname) {
  return !!(navItem.childItems || []).some((c) => `/${c.url}` === pathname);
}

function NavGroup({ item, onNavigate, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const hasChildren = item.childItems && item.childItems.length > 0;

  return (
    <div className="sidebar-nav-group">
      <button type="button" className="sidebar-nav-toggle" onClick={() => setOpen(!open)}>
        <span className="sidebar-tree-dot" />
        {item.name}
        {hasChildren && (
          <svg className="chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
          </svg>
        )}
      </button>
      {open && hasChildren && (
        <div className="sidebar-children">
          {item.childItems.map((child) => (
            <ChildLink key={child.id} item={child} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarItemBlock({ item, onNavigate, pathname }) {
  const hasNav = item.navItems && item.navItems.length > 0;
  const sectionActive = hasNav && item.navItems.some((nav) => groupContainsPath(nav, pathname));
  // Starts open only if the current page lives somewhere under this section —
  // every other section starts collapsed, matching the old app's sidebar
  // (top-level items are their own collapsible toggle, not an always-expanded
  // header over a permanently-visible group list).
  const [open, setOpen] = useState(!!sectionActive);

  if (!hasNav) {
    // Flat sidebar item (e.g. "dashboard") — no children, links directly.
    return (
      <NavLink to={`/${item.url}`} className="sidebar-flat-link" activeClassName="active" onClick={onNavigate}>
        <SidebarIcon iconClass={item.iconClass} />
        {item.name}
      </NavLink>
    );
  }

  return (
    <div className="sidebar-section">
      <button
        type="button"
        className={`sidebar-section-title ${sectionActive ? "active" : ""}`}
        onClick={() => setOpen(!open)}
      >
        <SidebarIcon iconClass={item.iconClass} />
        <span className="sidebar-section-title-text">{item.name}</span>
        <svg className={`chevron ${open ? "open" : ""}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="sidebar-tree">
          {item.navItems.map((nav) => (
            <NavGroup key={nav.id} item={nav} onNavigate={onNavigate} defaultOpen={groupContainsPath(nav, pathname)} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Collapsible group of the 14 generic settings pages + teacher-sections, nested under Administration. */
function SettingsNavGroup({ onNavigate }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="sidebar-nav-group">
      <button type="button" className="sidebar-nav-toggle" onClick={() => setOpen(!open)}>
        <span className="sidebar-tree-dot" />
        Settings
        <svg className="chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="sidebar-children">
          {SETTINGS_RESOURCES.map((r) => (
            <NavLink
              key={r.key}
              to={`/admin/settings/${r.key}`}
              className="sidebar-child-link"
              activeClassName="active"
              onClick={onNavigate}
            >
              {r.label}
            </NavLink>
          ))}
          <NavLink
            to="/admin/settings/teacher-sections"
            className="sidebar-child-link"
            activeClassName="active"
            onClick={onNavigate}
          >
            Teacher Sections
          </NavLink>
        </div>
      )}
    </div>
  );
}

/**
 * Renders two distinct things, matching what digicampusbd actually does:
 *  1. The permission-filtered content menu from GET /api/menu (dynamic,
 *     data-driven — see MenuContext).
 *  2. A fixed "Administration" section (Users/Roles/Permissions/Settings/
 *     Institute Profile/Audit Log) shown only to super admins —
 *     digicampusbd never exposed these through the delegable Permission
 *     matrix (verified against the live schema), so they are not part of
 *     the dynamic menu either here.
 *
 * Below the tablet breakpoint this renders as an off-canvas drawer
 * (`open`/`onClose` control visibility + a backdrop) instead of a
 * permanent column — see Sidebar.css.
 */
export default function Sidebar({ open, onClose }) {
  const { user } = useAuth();
  const { menu, loading } = useMenu();
  const location = useLocation();

  // Auto-close the drawer after following a link on mobile; a no-op (and
  // harmless) on desktop where the sidebar is always visible.
  const handleNavigate = () => {
    if (onClose) onClose();
  };

  return (
    <>
      {open && <button type="button" className="sidebar-backdrop" aria-label="Close menu" onClick={onClose} />}
      <nav className={`sidebar ${open ? "open" : ""}`}>
        {loading && <div className="sidebar-loading">Loading menu…</div>}

        {menu.map((sidebarItem) => (
          <SidebarItemBlock key={sidebarItem.id} item={sidebarItem} onNavigate={handleNavigate} pathname={location.pathname} />
        ))}

        {user && user.superAdmin && (
          <div className="sidebar-section admin-section">
            <div className="sidebar-section-title">Administration</div>
            <div className="sidebar-tree">
            <NavLink to="/admin/users" className="sidebar-child-link" activeClassName="active" onClick={handleNavigate}>
              Users
            </NavLink>
            <NavLink to="/admin/roles" className="sidebar-child-link" activeClassName="active" onClick={handleNavigate}>
              Roles
            </NavLink>
            <NavLink
              to="/admin/permissions"
              className="sidebar-child-link"
              activeClassName="active"
              onClick={handleNavigate}
            >
              Permissions
            </NavLink>
            <SettingsNavGroup onNavigate={handleNavigate} />
            <NavLink
              to="/admin/class-configurations"
              className="sidebar-child-link"
              activeClassName="active"
              onClick={handleNavigate}
            >
              Class Configuration
            </NavLink>
            <NavLink
              to="/admin/attendance-scheduler"
              className="sidebar-child-link"
              activeClassName="active"
              onClick={handleNavigate}
            >
              Attendance Scheduler
            </NavLink>
            <NavLink to="/admin/zk-devices" className="sidebar-child-link" activeClassName="active" onClick={handleNavigate}>
              ZKTeco Devices
            </NavLink>
            {isPlatformOperator(user) && (
              <>
                <NavLink
                  to="/admin/platform-institutes"
                  className="sidebar-child-link"
                  activeClassName="active"
                  onClick={handleNavigate}
                >
                  Institute Management
                </NavLink>
                <NavLink
                  to="/admin/institute-billing"
                  className="sidebar-child-link"
                  activeClassName="active"
                  onClick={handleNavigate}
                >
                  Institute Billing
                </NavLink>
                <NavLink
                  to="/admin/bkash-config"
                  className="sidebar-child-link"
                  activeClassName="active"
                  onClick={handleNavigate}
                >
                  bKash Configuration
                </NavLink>
              </>
            )}
            <NavLink
              to="/admin/online-fee-config"
              className="sidebar-child-link"
              activeClassName="active"
              onClick={handleNavigate}
            >
              Online Fee Payment Config
            </NavLink>
            <NavLink
              to="/admin/online-admission-settings"
              className="sidebar-child-link"
              activeClassName="active"
              onClick={handleNavigate}
            >
              Online Admission Settings
            </NavLink>
            <NavLink
              to="/admin/online-admission-applicants"
              className="sidebar-child-link"
              activeClassName="active"
              onClick={handleNavigate}
            >
              Online Admission Applicants
            </NavLink>
            <NavLink to="/admin/lottery" className="sidebar-child-link" activeClassName="active" onClick={handleNavigate}>
              Lottery
            </NavLink>
            <NavLink
              to="/admin/institute-profile"
              className="sidebar-child-link"
              activeClassName="active"
              onClick={handleNavigate}
            >
              Institute Profile
            </NavLink>
            <NavLink to="/admin/audit-log" className="sidebar-child-link" activeClassName="active" onClick={handleNavigate}>
              Audit Log
            </NavLink>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
